import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { createHttpOptions } from '../utils/http-utils';
import { switchMap, of, Observable } from 'rxjs';
import { ProfileDto } from '@app/dtos/user-profile.dto';

export enum NickyType {
  None = 'None',
  Email = 'Email',
  Domain = 'Domain',
  ShortId = 'ShortId',
}

export interface DomainInfo {
  /** Domain name */
  name: string | null;
  /** Indicates if the DNS setup is valid */
  isValid: boolean;
}

export interface NickSearchResult {
  userId: string;
  shortId: string | null;
  nick: string | null;
  nickyType: NickyType;
  publicName: string | null;
  websiteUrl: string | null;
  domains: DomainInfo[] | null;
  hasProfilePicture: boolean;
  profileImageUrl?: any;
  exactlyMatch?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private http = inject(HttpClient);
  public auth = inject(AuthService);

  private buildUrl(endpoint: string, queryParam: string, queryValue: string): string {
    return `${environment.api}${endpoint}?${queryParam}=${queryValue}`;
  }

  generalSearch(query: string): Observable<NickSearchResult[]> {
    return this.http.get<NickSearchResult[]>(this.buildUrl('Nick/search', 'q', query), createHttpOptions());
  }

  favoritesSearch(query: string) {
    return this.http.get(this.buildUrl('FavoriteNick/search', 'q', query), createHttpOptions());
  }

  getPublicProfileByShortId(shortId: string): Observable<ProfileDto> {
    return this.http.get<ProfileDto>(
      this.buildUrl('User/profile-public-shortid', 'shortId', shortId),
      createHttpOptions(),
    );
  }

  getPublicProfileByEmail(email: string): Observable<ProfileDto | null> {
    return this.generalSearch(email).pipe(
      switchMap((results: any) => {
        if (results[0]?.shortId) {
          return this.getPublicProfileByShortId(results[0].shortId);
        }
        return of(null);
      }),
    );
  }

  getPublicProfileByDomain(domain: string) {
    return this.http.get(this.buildUrl('User/profile-public-domain', 'domain', domain), createHttpOptions());
  }

  uuidPublicProfile(UUID: string) {
    return this.http.get(this.buildUrl('User/profile-public', 'userId', UUID), createHttpOptions());
  }
}
